import { type SupplierUpdateInput } from '../../../generated';

/** A value read back from GraphQL: nested objects, `__typename`, nulls. */
type StoredValue = unknown;

/**
 * Drops `__typename` and normalises null to undefined, all the way down.
 *
 * The response carries `__typename` on every object and the input carries none,
 * so a raw comparison of the two never matches.
 */
const canonicalise = (value: StoredValue): unknown => {
  if (value === null || value === undefined) return undefined;
  if (Array.isArray(value)) return value.map(canonicalise);
  if (typeof value !== 'object') return value;

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([key]) => key !== '__typename')
      .map(([key, nested]) => [key, canonicalise(nested)])
      .filter(([, nested]) => nested !== undefined)
  );
};

/**
 * Which channel an integration entry describes, on either side of the wire.
 *
 * The two shapes disagree: a save sends one entry with `email`, `whatsApp` and
 * `sms` keys, while the response is an array of union members
 * (`IntegrationEmailOption`, …). Both are flattened to `channel -> settings` so
 * they can be compared.
 */
const INTEGRATION_CHANNELS: Record<string, string> = {
  IntegrationEmailOption: 'email',
  IntegrationWhatsAppOption: 'whatsApp',
  IntegrationSmsOption: 'sms',
  IntegrationFaxOption: 'fax',
};

const flattenIntegration = (value: StoredValue): string | undefined => {
  if (!Array.isArray(value)) return undefined;

  const channels: Record<string, unknown> = {};
  let recognised = true;

  value.forEach((entry) => {
    if (!entry || typeof entry !== 'object') {
      recognised = false;
      return;
    }

    const record = entry as Record<string, unknown>;
    const typename = record.__typename;

    if (typeof typename === 'string') {
      // Response side: one union member per channel.
      const channel = INTEGRATION_CHANNELS[typename];
      if (!channel) {
        recognised = false;
        return;
      }
      channels[channel] = canonicalise(record);
      return;
    }

    // Input side: one entry carrying several channels.
    Object.entries(record).forEach(([channel, settings]) => {
      if (!Object.values(INTEGRATION_CHANNELS).includes(channel)) {
        recognised = false;
        return;
      }
      if (settings !== undefined) channels[channel] = canonicalise(settings);
    });
  });

  if (!recognised) return undefined;

  return JSON.stringify(
    Object.keys(channels)
      .sort()
      .map((channel) => [channel, channels[channel]])
  );
};

/**
 * Input keys the `Supplier` type spells differently.
 *
 * Only one today. It is a map rather than a special case so the next one is
 * visible: an unmapped mismatch reads the wrong key, finds nothing, and reports
 * a save that landed as failed.
 */
const RESPONSE_KEY_ALIASES: Record<string, string> = {
  enableFractionalUnits: 'hasEnabledFractionalUnits',
};

/**
 * Whether everything a save carried is already on the stored distributor.
 *
 * `supplierService` commits the row and then awaits an SNS publication, so a
 * publication failure comes back as an error with no `supplierUpdate` payload:
 * indistinguishable from a refused save. Retrying one of those writes again,
 * updates the bot user again, and emits the side effects twice, so a save that
 * landed must not be offered for retry.
 *
 * Anything this cannot compare confidently reads as "not confirmed", which
 * keeps the failure path as the default. Confirming too little costs a retry the
 * operator chose to make; confirming too much loses a save silently.
 */
export const isSupplierUpdateApplied = (
  input: Omit<SupplierUpdateInput, 'id'>,
  stored: Record<string, unknown> | null | undefined
): boolean => {
  if (!stored) return false;

  return Object.entries(input).every(([key, sent]) => {
    // `isTest` rides along on every save whether or not it changed, so it says
    // nothing about whether this one landed.
    if (key === 'isTest') return true;
    if (sent === undefined) return true;

    const found = stored[RESPONSE_KEY_ALIASES[key] ?? key];

    // A null or an empty string is a clear. The backend may store a cleared
    // field as absent (`code`, `externalName`) or as `''`, so either landed if
    // nothing is stored any more.
    if (sent === null || sent === '') {
      return found === null || found === undefined || found === '';
    }

    if (key === 'integration') {
      const sentChannels = flattenIntegration(sent);
      const foundChannels = flattenIntegration(found);
      // An unrecognised shape must not read as a match.
      return sentChannels !== undefined && sentChannels === foundChannels;
    }

    if (typeof sent === 'object') {
      return (
        JSON.stringify(canonicalise(found)) ===
        JSON.stringify(canonicalise(sent))
      );
    }

    return found === sent;
  });
};
